import React, {
  createContext,
  useEffect,
  useState,
  useMemo,
  useContext,
} from "react";
import { useQuery } from "@apollo/client/react";
import PropTypes from "prop-types";
import { useUserPerson } from "src/contexts/global/GlobalContext";
import { useGroupTypes } from "src/contexts/global/WorkspaceContext";
import { useOverlayOptionId } from "src/pages/Designs/overlay/useOverlayOptionId";
import {
  OPTION_PAGE_READ_POLICY,
  useRefetchAfterOptionWrites,
} from "src/pages/Designs/overlay/optionPageRead";

export { PAGE_TITLE_CONFIGS } from "./pageTitleConfig";

const helmetContext = createContext({});

function isDocumentNode(query) {
  return query && typeof query === "object" && Array.isArray(query.definitions);
}

const PageTitlePropTypes = {
  id: PropTypes.string,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      query: PropTypes.object,
      optionQuery: PropTypes.object,
      mapper: PropTypes.func,
    }),
  ]),
};

const QueriedPageTitle = ({ title, id }) => {
  const userPerson = useUserPerson();
  const { setPageTitle } = useContext(helmetContext);
  const groupTypes = useGroupTypes();

  const { query, optionQuery, mapper, isDashboard } = title;
  const overlayOptionId = useOverlayOptionId();
  const optionRead = Boolean(overlayOptionId && optionQuery);

  const { data, refetch } = useQuery(optionRead ? optionQuery : query, {
    variables: optionRead ? { id, optionId: overlayOptionId } : { id },
    ...(optionRead ? OPTION_PAGE_READ_POLICY : { fetchPolicy: "cache-first" }),
    skip: isDashboard,
  });
  useRefetchAfterOptionWrites(refetch, optionRead && !isDashboard);

  useEffect(() => {
    setPageTitle(mapper(isDashboard ? userPerson : data, id, groupTypes));
  }, [data, mapper, id, setPageTitle, isDashboard, userPerson, groupTypes]);

  return null;
};

QueriedPageTitle.propTypes = PageTitlePropTypes;

export const PageTitle = (props) => {
  const { title } = props;
  const { setPageTitle } = useContext(helmetContext);

  // Only set page title from title when it's a string (never set object → avoids "[object Object]").
  // When title is an object with invalid/missing query, clear title so we don't leave a stale one.
  useEffect(() => {
    if (typeof title === "string") {
      setPageTitle(title);
    } else if (
      title &&
      typeof title === "object" &&
      !isDocumentNode(title.query)
    ) {
      setPageTitle("");
    }
  }, [title, setPageTitle]);

  // Only render QueriedPageTitle when query is a valid DocumentNode (avoids Apollo reading undefined.definitions).
  const hasValidQuery = isDocumentNode(title?.query);

  if (hasValidQuery) {
    return <QueriedPageTitle {...props} />;
  }

  return null;
};

PageTitle.propTypes = PageTitlePropTypes;

const defaultSiteTitle = process.env.REACT_APP_TITLE || "";

const AppHelmet = ({ children }) => {
  const [pageTitle, setPageTitle] = useState("");

  const value = useMemo(() => ({ setPageTitle }), []);
  const computedTitle = [defaultSiteTitle, pageTitle]
    .filter(Boolean)
    .join(" - ");

  // TFSS-416: react-helmet removed — SPA sets document.title only. For OG/SSR later, prefer React 19 metadata APIs.
  useEffect(() => {
    document.title = computedTitle;
  }, [computedTitle]);

  return (
    <helmetContext.Provider value={value}>{children}</helmetContext.Provider>
  );
};

AppHelmet.propTypes = {
  children: PropTypes.node,
};

export default AppHelmet;
